import InfoCircle from "@iewc/MobiusOverrides/Icons/InfoCircle";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@iewc/Overrides/Components/ProductPartNumbers";
import setInventoryResultsModalIsOpen from "@iewc/Store/Handlers/Cart/CustomSetInventoryResultsModal";
import setRedirectedFromCheckout from "@iewc/Store/Handlers/CheckoutShipping/CustomSetRedirected";
import updateValidCartLine from "@iewc/Store/Handlers/CheckoutShipping/CustomUpdateValidCartLines";
import { CustomState } from "@iewc/Store/Reducers";
import { colorStrings, mediaQueriesBP } from "@iewc/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { css } from "styled-components";

interface OwnProps {
    extendedStyles?: InventoryResultsModalStyles;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: CustomState) => {
    const cartState = getCurrentCartState(state);
    const cartLines = cartState.value?.cartLines;
    const cartLinesValidation = state.checkoutShipping.cartLines;

    return {
        cartLines,
        cartLinesValidation,
        modalIsOpen: state.customInventoryResultsModal.isOpen,
        productInfos: state.customInventoryResultsModal.productInfos,
    };
};

const mapDispatchToProps = {
    setInventoryResultsModalIsOpen,
    updateCartLine,
    removeCartLine,
    updateValidCartLine,
    setRedirectedFromCheckout,
};

export interface InventoryResultsModalStyles {
    modal?: ModalPresentationProps;
    innerWrapper?: InjectableCss;
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    container?: GridContainerProps;
    containerOuter?: InjectableCss;
    productImageGridItem?: GridItemProps;
    orderLineInfoGridItem?: GridItemProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productPartNumbersGridItem?: GridItemProps;
    buttonLinksLabels?: LinkPresentationProps;
    addToCartValidationDescription?: TypographyProps;
    titleIcon?: IconProps;
    tooltip?: TooltipPresentationProps;
    actionBtnsGridItem?: GridItemProps;
    modalTitleHeadline?: TypographyProps;
    popupWrapper?: InjectableCss;
    popupMessageText?: TypographyProps;
    popupMessageTitle?: TypographyProps;
    closeButton?: ButtonPresentationProps;
    actionLink?: LinkPresentationProps;
    containerPopUp?: InjectableCss;
    containerActionBtns?: InjectableCss;
    wrapperStylesItem?: InjectableCss;
    addToCartLinkGridContainer?: GridContainerProps;
    addToCartLabelProperty?: TypographyProps;
    addToCartLabelsWrapper?: InjectableCss;
}

export const inventoryResultsModalStyles: InventoryResultsModalStyles = {
    modal: {
        size: 600,
        // sizeVariant: "medium",
        cssOverrides: {
            modalContainer: css`
                padding: 0 15px;

                ${mediaQueriesBP.minLG} {
                    padding: 0;
                }
            `,
            modalBody: css`
                max-height: 400px;
                background-color: ${getColor(colorStrings.commonGlobalWhite)};
                overflow-y: hidden;
            `,
            modalTitle: css`
                padding: 10px 20px;
            `,
            modalContent: css`
                padding: 20px;
                max-height: 440px;
                overflow-y: auto;

                &&&& .cartLine-validation + .cartLine-validation {
                    padding-top: 15px;
                    border-top: 1px solid ${getColor("common.border")};
                    margin-block-start: 10px;
                }

                ${mediaQueriesBP.minLG} {
                    padding: 20px 50px 10px !important;
                }
            `,
        },
    },

    modalTitleHeadline: {
        as: "h2",
        weight: "bold",
        css: css`
            display: flex;
            align-items: center;
        `,
    },

    containerOuter: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            gap: 16px;

            .transition-enter {
                opacity: 0;
            }
            .transition-enter-active {
                opacity: 0;
                -webkit-animation: fade-out 1s ease-out both;
                animation: fade-out 1s ease-out both;
            }

            &.transition-exit {
                opacity: 1;
            }

            &.transition-exit-active {
                opacity: 0;
                -webkit-animation: fade-out 1s ease-out both;
                animation: fade-out 1s ease-out both;
            }

            @keyframes fade-out {
                0% {
                    opacity: 1;
                }
                100% {
                    opacity: 0;
                }
            }
        `,
    },

    container: {
        gap: 10,
        css: css`
            padding: 0;
        `,
    },

    innerWrapper: {
        css: css``,
    },

    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },

    productImage: {
        linkWrappingImage: {
            css: css`
                pointer-events: none;
            `,
        },
        img: {
            css: css`
                max-height: 83px;
                max-width: 83px;
                padding: 0;
                border: 1px solid ${getColor(colorStrings.commonBorder)};
            `,
        },
    },

    productBrandAndDescriptionGridItem: {
        width: 12,
    },

    productPartNumbersGridItem: {
        width: 12,
    },

    productDescription: {
        productDetailLink: {
            typographyProps: {
                size: 14,
                lineHeight: "24px",
                weight: "bold",
                css: css`
                    margin-bottom: 5px;
                `,
            },
        },
    },

    productPartNumbers: {
        condensed: true,
        container: {
            gap: 0,
            offsetCss: css`
                flex-direction: column;
            `,
        },

        erpNumberLabelText: {
            weight: "bold",
            size: 14,
            lineHeight: "24px",
            css: css`
                flex: 0 0 auto;
            `,
        },
        erpNumberValueText: {
            size: 14,
            lineHeight: "24px",
            css: css`
                flex: 1 1 auto;
                min-width: 0;
            `,
        },
        customerNameLabelText: {
            weight: "bold",
            size: 14,
            lineHeight: "24px",
            css: css`
                flex: 0 0 auto;
            `,
        },
        customerNameValueText: {
            size: 14,
            lineHeight: "24px",
            css: css`
                flex: 1 1 auto;
                min-width: 0;
            `,
        },
        manufacturerItemLabelText: {
            size: 14,
            lineHeight: "24px",
            weight: "bold",
            css: css`
                flex: 0 0 auto;
            `,
        },
        manufacturerItemValueText: {
            size: 14,
            lineHeight: "24px",
            css: css`
                flex: 1 1 auto;
                min-width: 0;
            `,
        },

        customerNameGridItem: {
            width: 12,
        },
        manufacturerItemGridItem: {
            width: 12,
        },
    },

    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
    },

    orderLineInfoContainer: {
        gap: 0,
        offsetCss: css`
            gap: 10px;
        `,
    },

    addToCartValidationDescription: {
        size: 14,
        lineHeight: "24px",
        weight: "bold",
    },

    buttonLinksLabels: {
        css: css`
            min-width: 100%;
            z-index: ${getProp("theme.zIndex.toaster")};

            &.addAvailableQtyLink {
                padding-left: 2px;
            }

            ${mediaQueriesBP.minLG} {
                &.start span {
                    text-align: left;
                }

                &.center span {
                    text-align: center;
                }
            }
        `,
        typographyProps: {
            size: 14,
            lineHeight: "24px",
            css: css`
                width: 100%;
                display: block;
            `,
        },
    },

    titleIcon: {
        src: "Info",
        color: colorStrings.primaryMain,
        size: 40,
        css: css`
            /* width: 24px; */
            display: inline-block;
            text-align: center;
            vertical-align: middle;
            margin: 0 20px 0 0;
        `,
    },

    tooltip: {
        iconProps: {
            size: 12,
            color: colorStrings.primaryMain,
        },
        cssOverrides: {
            tooltipContainer: css`
                left: 0;
                justify-content: flex-start;
                transform: translateX(0);
                margin: 0;
                bottom: 130%;
            `,
            tooltipArrow: css`
                left: 15px;
            `,
            tooltipClickable: css`
                padding: 0 2px 0;
            `,
        },
    },

    actionBtnsGridItem: {
        css: css`
            flex: 1 1 100%;
            padding: 0;
            max-width: max-content;
            min-width: 100%;
            align-items: center;

            &.addAvailableQty {
                position: relative;
            }

            ${mediaQueriesBP.minLG} {
                flex: 1 1 32%;
                min-width: 32%;
            }
        `,
    },

    containerPopUp: {
        css: css`
            position: absolute;
            height: 40px;
            width: 100%;
            max-width: 250px;
            pointer-events: none;
            top: 0;
            left: -5px;
        `,
    },

    popupWrapper: {
        css: css`
            background-color: ${getColor(colorStrings.commonGlobalWhite)};
            border: 1px solid ${getColor(colorStrings.commonBorder)};
            width: 220px;
            position: absolute;
            display: flex;
            flex-direction: column;
            padding: 10px;
            box-shadow: 0 3px 5px 0 rgba(25, 25, 25, 0.2);
            bottom: calc(100% + 10px);
            pointer-events: all;

            &::before {
                content: "";
                position: absolute;
                top: 100%;
                left: 7%;
                margin-left: -10px;
                border: 10px solid transparent;
                border-top-color: ${getColor(colorStrings.commonGlobalWhite)};
            }
        `,
    },

    popupMessageTitle: {
        size: "12px",
        weight: 700,
        css: css`
            margin-bottom: 5px;
        `,
    },

    popupMessageText: {
        size: "12px",
        weight: 400,
        css: css`
            margin-bottom: 5px;
        `,
    },

    closeButton: {
        css: css`
            position: absolute;
            top: 0;
            right: 10px;
            cursor: pointer;
            background-color: transparent;
            border: none;
            height: 10px;
            width: 10px;
            min-width: unset;
            min-height: unset;
            max-width: 20px;
            padding: 0;

            &::before {
                position: absolute;
                content: "";
                width: 100%;
                border-top: 1px solid ${getColor(colorStrings.textMain)};
                transform: rotate(45deg) translate(3px, 3px);
            }

            &::after {
                position: absolute;
                content: "";
                width: 100%;
                border-top: 1px solid ${getColor(colorStrings.textMain)};
                transform: rotate(-45deg) translate(-3px, 3px);
            }

            &:hover {
                background-color: transparent;
            }
        `,
    },

    containerActionBtns: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            gap: 10px;
            inline-size: 100%;

            ${mediaQueriesBP.minMD} {
                gap: 30px;
                align-items: center;
                flex-direction: row;
                justify-content: flex-end;
            }
        `,
    },

    wrapperStylesItem: {
        css: css`
            padding: 0;
            max-width: max-content;

            &.addAvailableQty {
                position: relative;
                display: flex;
                align-items: center;
            }
        `,
    },

    addToCartLinkGridContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            padding: 16px;
            border: 1px solid ${getColor(colorStrings.commonGlobalGray03)};
            background: ${getColor(colorStrings.commonGlobalGray05)};
            justify-content: flex-start;
            min-block-size: 75px;
        `,
        offsetCss: css`
            inline-size: 100%;
            gap: 4px;
            margin: 0;
        `,
    },

    addToCartLabelProperty: {
        size: 12,
        lineHeight: "16px",
        color: colorStrings.textMain,
    },

    addToCartLabelsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;

            ${mediaQueriesBP.minMD} {
                gap: 16px;
                flex-direction: row;
                align-items: center;
            }
        `,
    },
};

export const LastUpdatedListIdCookieName = "LastUpdatedListId";

const InventoryResultsModal = ({
    productInfos,
    cartLines,
    cartLinesValidation,
    modalIsOpen,
    setInventoryResultsModalIsOpen,
    updateCartLine,
    removeCartLine,
    updateValidCartLine,
    extendedStyles,

    setRedirectedFromCheckout,
}: Props) => {
    const [styles] = useState(() => mergeToNew(inventoryResultsModalStyles, extendedStyles));
    const [inProgress, setInProgress] = useState(false);
    const [cartLinesToBeReviewed, setCartLinesToBeReviewed] = useState<CartLineModel[]>([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [nodeRefs, setNodeRefs] = useState<React.RefObject<HTMLDivElement>[]>([]);
    const [popUpID, setPopUpID] = useState<string>("");

    useEffect(() => {
        if (!cartLines || cartLines?.length === 0 || !cartLinesValidation || cartLinesValidation?.length === 0) {
            setInProgress(false);
            setTimeout(() => {
                setInventoryResultsModalIsOpen({ modalIsOpen: false });
            }, 1000);
            return;
        }

        const cartLinesToBeReviewed = cartLines?.filter(o => {
            return cartLinesValidation.find((p: any) => p.material === o.erpNumber || p.material === o.manufacturerItem)
                ? o
                : null;
        });

        setNodeRefs(nodeRefs =>
            Array(cartLinesToBeReviewed.length)
                .fill(null)
                .map((_, i) => nodeRefs[i] ?? React.createRef()),
        );

        setInProgress(false);
        setCartLinesToBeReviewed(cartLinesToBeReviewed ?? []);
    }, [cartLinesValidation]);

    if (!productInfos) {
        return null;
    }

    const modalCloseHandler = () => {
        setInProgress(false);
        setInventoryResultsModalIsOpen({ modalIsOpen: false });
        setRedirectedFromCheckout({ isRedirectedFromCheckout: false });
    };

    const requestAQuoteHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, cartLine: CartLineModel) => {
        setInProgress(true);

        if (cartLine) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        ...cartLine.properties,
                        isQuotedByUser: "true",
                    },
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    setInProgress(false);

                    if (!cartLines || cartLines?.length === 0) {
                        modalCloseHandler();
                        return;
                    }

                    updateValidCartLine({
                        cartLineERP: cartLine.erpNumber,
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const addAvailableQtyClickHandler = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        cartLine: CartLineModel,
        availQty: number,
    ) => {
        e.preventDefault();

        setInProgress(true);

        if (cartLine) {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered: availQty,
                },
                shouldNotReloadPromotions: true,
                shouldNotLoadFullCart: true,
                onSuccess: () => {
                    setInProgress(false);

                    if (!cartLines || cartLines?.length === 0) {
                        modalCloseHandler();
                        return;
                    }
                    updateValidCartLine({
                        cartLineERP: cartLine.erpNumber,
                    });
                },
                onComplete(resultProps) {
                    if (resultProps.apiResult) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.();
                    }
                },
            });
        }
    };

    const removeClickHandler = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, cartLine: CartLineModel) => {
        e.preventDefault();

        setInProgress(true);

        if (cartLine) {
            removeCartLine({ cartLineId: cartLine.id });
            updateValidCartLine({
                cartLineERP: cartLine.erpNumber,
            });
        }
    };

    return (
        <Modal
            {...styles.modal}
            headline={
                <Typography {...styles.modalTitleHeadline}>
                    <Icon {...styles.titleIcon} />
                    {siteMessage("AddToCart_Validation_Title")}
                </Typography>
            }
            isOpen={modalIsOpen}
            handleClose={modalCloseHandler}
        >
            <TransitionGroup {...styles.innerWrapper} data-test-selector="inventoryResultsModal">
                {cartLinesToBeReviewed?.map((cartLine, index) => {
                    let availQty = 0;
                    let numberOfSpools = 0;
                    let spoolsLength = 0;
                    let spoolsLengthDisplay = "";
                    const hasSpoolSelected = +cartLine.properties["spoolSelected"] > 0;

                    const cartLineValidation = cartLinesValidation.find(
                        (p: any) => p.material === cartLine.erpNumber || p.material === cartLine.manufacturerItem,
                    );

                    if (!cartLineValidation) {
                        return null;
                    }

                    const { putupAvailableCount, availableQuantity } = cartLineValidation;

                    if (hasSpoolSelected) {
                        availQty = parseFloat(availableQuantity) || 0;
                        numberOfSpools = parseFloat(putupAvailableCount) || 0;

                        spoolsLength = numberOfSpools > 0 ? availQty / numberOfSpools : 0;
                        spoolsLengthDisplay = `${spoolsLength.toLocaleString("en", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0,
                        })} ${cartLine.unitOfMeasure}`;
                    } else {
                        availQty = +availableQuantity ?? "";
                    }

                    const availableQtyDisplay = `${availQty.toLocaleString("en", {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                    })} ${cartLine.unitOfMeasure}`;

                    return (
                        <CSSTransition
                            nodeRef={nodeRefs[index]}
                            key={cartLine.productId!}
                            timeout={700}
                            classNames="transition"
                        >
                            <StyledWrapper
                                ref={nodeRefs[index]}
                                {...styles.containerOuter}
                                className="cartLine-validation"
                            >
                                <GridItem width={12}>
                                    <GridContainer {...styles.container}>
                                        <GridItem {...styles.productImageGridItem}>
                                            <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                                        </GridItem>
                                        <GridItem {...styles.orderLineInfoGridItem}>
                                            <GridContainer {...styles.orderLineInfoContainer}>
                                                <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                                    <Typography {...styles.productDescription}>
                                                        {cartLine.shortDescription}
                                                    </Typography>
                                                </GridItem>
                                                <GridItem {...styles.productPartNumbersGridItem}>
                                                    <ProductPartNumbers
                                                        productNumber={cartLine.erpNumber}
                                                        customerProductNumber={cartLine.customerName}
                                                        manufacturerItem={cartLine.manufacturerItem}
                                                        extendedStyles={styles.productPartNumbers}
                                                        // Verndale Customization
                                                        showManufacturerItem={!cartLine.properties["isc_isCmiProduct"]}
                                                        cmiCustomerNumber={cartLine.properties["isc_cmiCustomer"] ?? ""}
                                                        modelNumber={cartLine.properties["product_modelNumber"] ?? ""}
                                                        cmiIewcMaterial={
                                                            cartLine.properties["isc_cmiIewcMaterial"] ?? ""
                                                        }
                                                        // END Verndale Customization
                                                    />
                                                </GridItem>
                                            </GridContainer>
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                <GridItem width={12}>
                                    <Typography {...styles.addToCartValidationDescription}>
                                        {siteMessage("AddToCart_Validation_Description")}
                                    </Typography>
                                </GridItem>
                                {+availQty > 0 && (
                                    <GridItem width={12}>
                                        <GridContainer {...styles.addToCartLinkGridContainer}>
                                            <GridItem width={12} style={{ padding: 0 }}>
                                                <StyledWrapper
                                                    {...styles.wrapperStylesItem}
                                                    className="btn-action addAvailableQty"
                                                >
                                                    <Icon
                                                        src={InfoCircle}
                                                        size={14}
                                                        color={colorStrings.primaryMain}
                                                        onClick={() => {
                                                            setPopUpID(cartLine.id);
                                                            setIsPopupOpen(!isPopupOpen);
                                                        }}
                                                    />

                                                    <StyledWrapper {...styles.containerPopUp}>
                                                        {isPopupOpen && popUpID === cartLine.id && (
                                                            <StyledWrapper
                                                                className="popup-wrapper"
                                                                {...styles.popupWrapper}
                                                            >
                                                                <Button
                                                                    {...styles.closeButton}
                                                                    onClick={() => setIsPopupOpen(false)}
                                                                    title="Close Popup"
                                                                ></Button>
                                                                <Typography {...styles.popupMessageTitle}>
                                                                    {siteMessage("AddToCart_Validation_Title_Tooltip")}
                                                                </Typography>
                                                                <Typography {...styles.popupMessageText}>
                                                                    {siteMessage(
                                                                        "AddToCart_Validation_Content_Tooltip",
                                                                    )}
                                                                </Typography>
                                                            </StyledWrapper>
                                                        )}
                                                    </StyledWrapper>

                                                    <Link
                                                        {...styles.buttonLinksLabels}
                                                        className={`addAvailableQtyLink ${
                                                            +availQty > 0 ? "start" : "center"
                                                        }`}
                                                        onClick={e => {
                                                            let cartlineDup = { ...cartLine };
                                                            if (hasSpoolSelected) {
                                                                cartlineDup = {
                                                                    ...cartlineDup,
                                                                    properties: {
                                                                        ...cartlineDup.properties,
                                                                        spoolSelected: `${numberOfSpools}`,
                                                                        spoolLength: `${spoolsLength}`,
                                                                    },
                                                                };
                                                            }

                                                            addAvailableQtyClickHandler(
                                                                e as any,
                                                                cartlineDup,
                                                                +availQty,
                                                            );
                                                        }}
                                                        disabled={inProgress}
                                                    >
                                                        {`${translate("Add Available Quantity")}`}
                                                    </Link>
                                                </StyledWrapper>
                                            </GridItem>
                                            <GridItem width={12} style={{ padding: 0 }}>
                                                <StyledWrapper {...styles.addToCartLabelsWrapper}>
                                                    <Typography {...styles.addToCartLabelProperty}>
                                                        {translate("Available Qty")}: {availableQtyDisplay}
                                                    </Typography>

                                                    {hasSpoolSelected && (
                                                        <>
                                                            <Typography {...styles.addToCartLabelProperty}>
                                                                {translate("Number of Spools")}: {numberOfSpools}
                                                            </Typography>
                                                            <Typography {...styles.addToCartLabelProperty}>
                                                                {translate("Spool Length")}: {spoolsLengthDisplay}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                )}
                                <GridItem width={12}>
                                    <StyledWrapper {...styles.containerActionBtns}>
                                        <StyledWrapper>
                                            <Link
                                                {...styles.buttonLinksLabels}
                                                className={`${+availQty > 0 ? "center" : "start"}`}
                                                onClick={e => {
                                                    requestAQuoteHandler(e as any, cartLine);
                                                }}
                                                disabled={inProgress}
                                            >
                                                {translate("Request a Quote")}
                                            </Link>
                                        </StyledWrapper>
                                        <StyledWrapper>
                                            <Link
                                                {...styles.buttonLinksLabels}
                                                className="center"
                                                onClick={e => {
                                                    removeClickHandler(e as any, cartLine);
                                                }}
                                                disabled={inProgress}
                                            >
                                                {translate("Remove from Cart")}
                                            </Link>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </GridItem>
                            </StyledWrapper>
                        </CSSTransition>
                    );
                })}
            </TransitionGroup>
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(InventoryResultsModal);
